import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { FilePlus2, RefreshCw, ArrowRight, FileText, FileStack, Users, TrendingUp } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";

export default function Dashboard() {
  const { user, activeWorkspace } = useAuth();
  const [docs, setDocs] = useState([]);
  const [tpls, setTpls] = useState([]);
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  useEffect(() => {
    if (!activeWorkspace) return;
    api.get(`/workspaces/${activeWorkspace.id}/documents`).then((r) => setDocs(r.data || []));
    api.get(`/workspaces/${activeWorkspace.id}/templates`).then((r) => setTpls(r.data || []));
  }, [activeWorkspace]);

  if (!activeWorkspace) return null;

  return (
    <div className="max-w-6xl mx-auto p-8" data-testid="dashboard">
      <div className="mb-8">
        <div className="mono text-xs uppercase tracking-widest text-cyan-300 mb-2">Workspace · {activeWorkspace.name}</div>
        <h1 className="font-display text-3xl font-bold text-white">{greet}, {user?.first_name}</h1>
        <p className="text-slate-400 mt-1">What would you like to document today?</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Link to="/app/new?mode=create" className="fx-panel p-6 fx-hover-panel group relative overflow-hidden" data-testid="dash-card-create">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full fx-glow-cyan opacity-30 group-hover:opacity-60 transition"/>
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/30 to-blue-500/30 border border-cyan-400/30 flex items-center justify-center"><FilePlus2 size={22} className="text-cyan-300"/></div>
          <div className="mt-4 font-display font-semibold text-lg text-white">Create new document</div>
          <p className="text-sm text-slate-400 mt-1">Turn notes, files and information into a company-standard document.</p>
          <div className="mt-4 text-cyan-300 text-sm inline-flex items-center gap-1">Start <ArrowRight size={14}/></div>
        </Link>
        <Link to="/app/new?mode=standardise" className="fx-panel p-6 fx-hover-panel group relative overflow-hidden" data-testid="dash-card-standardise">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full fx-glow-violet opacity-30 group-hover:opacity-60 transition"/>
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-500/30 to-purple-500/30 border border-violet-400/30 flex items-center justify-center"><RefreshCw size={22} className="text-violet-300"/></div>
          <div className="mt-4 font-display font-semibold text-lg text-white">Standardise existing document</div>
          <p className="text-sm text-slate-400 mt-1">Bring an existing document into the current company template and standards.</p>
          <div className="mt-4 text-violet-300 text-sm inline-flex items-center gap-1">Start <ArrowRight size={14}/></div>
        </Link>
      </div>

      <div className="grid md:grid-cols-4 gap-3 mt-8">
        <Stat icon={FileText} label="Documents" value={docs.length} color="text-cyan-300"/>
        <Stat icon={FileStack} label="Templates" value={tpls.length} color="text-blue-300"/>
        <Stat icon={TrendingUp} label="Needs review" value={docs.filter(d=>d.status==="needs_review").length} color="text-amber-300"/>
        <Stat icon={Users} label="Role" value={activeWorkspace.role} color="text-violet-300"/>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-8">
        <div className="fx-panel p-5">
          <div className="flex items-center justify-between mb-4"><div className="font-display font-semibold">Recent documents</div><Link to="/app/documents" className="text-xs text-cyan-300 hover:text-cyan-200">View all</Link></div>
          {docs.length === 0 ? (
            <EmptyRow text="Your generated documents will appear here." cta="Create document" to="/app/new" testid="empty-docs"/>
          ) : (
            <div className="divide-y divide-[#1E293B]">
              {docs.slice(0,6).map((d) => (
                <Link key={d.id} to={`/app/documents/${d.id}`} className="flex items-center justify-between py-3 hover:bg-white/5 -mx-2 px-2 rounded transition" data-testid={`recent-doc-${d.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-slate-100 truncate">{d.title}</div>
                    <div className="text-xs text-slate-500 mono">{d.document_number} · {d.document_type}</div>
                  </div>
                  <StatusBadge status={d.status}/>
                </Link>
              ))}
            </div>
          )}
        </div>
        <div className="fx-panel p-5">
          <div className="flex items-center justify-between mb-4"><div className="font-display font-semibold">Templates</div><Link to="/app/templates" className="text-xs text-cyan-300 hover:text-cyan-200">Manage</Link></div>
          {tpls.length === 0 ? (
            <EmptyRow text="Your templates teach Fexidoc how your organisation structures documents." cta="Add template" to="/app/templates" testid="empty-tpls"/>
          ) : (
            <div className="divide-y divide-[#1E293B]">
              {tpls.slice(0,6).map((t) => (
                <div key={t.id} className="flex items-center justify-between py-3">
                  <div className="min-w-0">
                    <div className="text-sm text-slate-100 truncate">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.category} · v{t.current_version}</div>
                  </div>
                  <div className="text-xs text-slate-500 mono">{t.usage_count || 0} uses</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({icon: Icon, label, value, color}) {
  return (
    <div className="fx-panel p-4">
      <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wider"><Icon size={14} className={color}/> {label}</div>
      <div className="mt-2 font-display text-2xl font-bold text-white capitalize">{value}</div>
    </div>
  );
}

function EmptyRow({text, cta, to, testid}) {
  return (
    <div className="text-center py-6" data-testid={testid}>
      <div className="text-sm text-slate-400">{text}</div>
      <Link to={to} className="mt-3 inline-block fx-btn-primary text-white text-sm font-medium px-4 py-2 rounded-lg">{cta}</Link>
    </div>
  );
}
